<script setup lang="ts">
import { onMounted, ref } from 'vue';

import UpsertEmployeeDialog from '@/components/dialogs/UpsertEmployeeDialog.vue';
import Link from '@/components/Link.vue';
import List from '@/components/List.vue';
import { useEmployeeStore } from '@/stores/employee';
import { useOrganizationStore } from '@/stores/organization';
import type { Employee } from '@/types/employee';
import type { Organization } from '@/types/organization';
import { toDisplayDate } from '@/utils/date';
import { processErrorWithFeedback } from '@/utils/error';
import { useRouter } from 'vue-router';

const router = useRouter();

const employeeStore = useEmployeeStore();
const organizationStore = useOrganizationStore();

const employees = ref<Employee[]>([]);
const organizations = ref<Organization[]>([]);
const isInitializing = ref(true);

const employeeListData = ref<any[]>([]);

// TODO: convert to viewmodel or something, this is very inpractical and should be done on the server-side
async function getInOrganizationTableData(): Promise<any> {
    // TODO: add concise return type
    try {
        employees.value = await employeeStore.fetch();
        organizations.value = await organizationStore.get();

        employeeListData.value = employees.value.map((employee) => {
            const assignedOrganizations = organizations.value.filter((organization) =>
                organization.employees.find((employeeInOrganization) => employeeInOrganization.employee == employee._id)
            );
            const assignedOrganizationNames = assignedOrganizations.map((organization) => organization.name);

            return {
                _id: employee._id,
                initials: employee.initials || '-',
                firstName: employee.firstName || '-',
                lastName: employee.lastName || '-',
                dateOfBirth: toDisplayDate(employee.dateOfBirth),
                email: employee.email || '-',
                fullName: `${employee.firstName} ${employee.lastName}`,
                jobTitle: employee.jobTitle ?? '-',
                currentOrganization: employee.currentOrganization,
                assignedOrganizations: assignedOrganizations,
                assignedOrganizationNames: assignedOrganizationNames || '-'
            };
        });
    } catch (error) {
        processErrorWithFeedback('Er is iets misgegaan tijdens het ophalen van de data', error);
    }
}

onMounted(async () => {
    await getInOrganizationTableData();
    isInitializing.value = false;
});

const employeeListHeaders = [
    { title: 'Voorletters', value: 'initials', sortable: true },
    { title: 'Naam', value: 'fullName', sortable: true },
    { title: 'Geboortedatum', value: 'dateOfBirth', sortable: true },
    { title: 'Email', value: 'email', sortable: true },
    { title: 'Functie', value: 'jobTitle', sortable: true },
    { title: 'Gekoppelde bedrijven', value: 'assignedOrganizations', sortable: true }
];

const isUpsertEmployeeDialogOpen = ref(false);

function openEmployeeProfile(employee: Employee) {
    router.push(`/medewerker/${employee._id}`);
}
</script>

<template>
    <div>
        <List
            entity-name="Medewerker"
            :headers="employeeListHeaders"
            :items="employeeListData"
            :isInitializing="isInitializing"
            :createButtonCallback="() => (isUpsertEmployeeDialogOpen = true)"
            @click-table-row="(item) => openEmployeeProfile(item)"
        >
            <template v-slot:item.fullName="{ item }">
                <span v-if="!item.fullName" class="text-red font-weight-bold">Ongeldig</span>
                <Link v-else :route-to="{ name: 'employee', params: { id: item._id } }" icon="person" :displayText="item.fullName" />
            </template>

            <template v-slot:item.assignedOrganizations="{ item }">
                <span v-if="item.assignedOrganizations.length < 1" class="text-red font-weight-bold">-</span>
                <Link
                    v-for="organization in item.assignedOrganizations"
                    :route-to="{ name: 'organization-profile', params: { organizationId: organization._id } }"
                    :key="organization._id"
                    icon="business"
                    :displayText="organization.name"
                />
            </template>
        </List>
        <UpsertEmployeeDialog v-model="isUpsertEmployeeDialogOpen" @closeDialog="isUpsertEmployeeDialogOpen = false" :is-insert="true" />
    </div>
</template>

<style scoped>
.icon-actionPoint {
    font-size: 25px;
    &:hover {
        cursor: pointer;
        filter: brightness(0.8);
        transition: all 0.15s ease-in-out;
    }
}
</style>
